// src/components/Chat/reportTagParser.js

/**
 * Parse report tags from AI message content
 * Handles both LLM-generated tags and orchestrator-injected tags
 */
export const parseReportTags = (messageText) => {
  if (!messageText || typeof messageText !== 'string') {
    return { sections: [], fullReports: [], metadata: [] };
  }

  const hasAnyTags = 
    messageText.includes('<report_section>') || 
    messageText.includes('<full_report>') || 
    messageText.includes('<report_save>') ||
    messageText.includes('REPORT_SAVED_TAG::');

  if (!hasAnyTags) {
    return { sections: [], fullReports: [], metadata: [] };
  }

  console.log('🔍 Parsing report tags from message (length:', messageText.length, ')');

  const sections = [];
  const fullReports = [];
  const metadata = [];

  // ── Parse orchestrator-injected REPORT_SAVED_TAG (most reliable) ──
  const savedTagRegex = /REPORT_SAVED_TAG::(\{[^}]+\})/g;
  let savedMatch;
  while ((savedMatch = savedTagRegex.exec(messageText)) !== null) {
    try {
      const saveData = JSON.parse(savedMatch[1]);
      if (saveData && saveData.s3Key) {
        fullReports.push({
          ...saveData,
          type: 'full_report',
          displayName: saveData.reportTitle || 'Research Report',
          // No URL yet — will be fetched via WebSocket
          needsPresignedUrl: true,
          id: `report_saved_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        });
        console.log('✅ Found saved report:', saveData.reportTitle, 'key:', saveData.s3Key);
      }
    } catch (error) {
      continue;
    }
  }

  // ── Parse <report_save> tags (LLM-generated) ──
  const reportSaveRegex = /<report_save>\s*(\{[\s\S]*?\})\s*<\/report_save>/gi;
  let reportSaveMatch;
  while ((reportSaveMatch = reportSaveRegex.exec(messageText)) !== null) {
    try {
      let saveData = null;
      try {
        saveData = JSON.parse(reportSaveMatch[1].trim());
      } catch (parseError) {
        saveData = attemptJsonFix(reportSaveMatch[1]);
      }

      if (saveData) {
        // If the LLM included s3Key, great. If not, construct it.
        if (!saveData.s3Key && saveData.sessionId && saveData.userName) {
          saveData.s3Key = `app_reports/${saveData.userName.toLowerCase()}/${saveData.sessionId}_report.md`;
        }

        // Only add if we have an s3Key AND we don't already have this report
        // (avoid duplicates with REPORT_SAVED_TAG)
        if (saveData.s3Key) {
          const alreadyFound = fullReports.some(r => r.s3Key === saveData.s3Key);
          if (!alreadyFound) {
            fullReports.push({
              ...saveData,
              type: 'full_report',
              displayName: saveData.reportTitle || saveData.displayName || 'Research Report',
              needsPresignedUrl: true,
              id: `report_save_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
            });
            console.log('✅ Found report_save:', saveData.reportTitle);
          }
        }
      }
    } catch (error) {
      continue;
    }
  }

  // ── Parse <report_section> tags ──
  const sectionRegex = /<report_section>\s*(\{[\s\S]*?\})\s*<\/report_section>/gi;
  let sectionMatch;
  while ((sectionMatch = sectionRegex.exec(messageText)) !== null) {
    try {
      let sectionData = null;
      try {
        sectionData = JSON.parse(sectionMatch[1]);
      } catch (parseError) {
        sectionData = attemptJsonFix(sectionMatch[1]);
      }

      if (sectionData && sectionData.s3Key && typeof sectionData.sectionNumber !== 'undefined') {
        sections.push({
          ...sectionData,
          type: 'section',
          needsPresignedUrl: !sectionData.url,
          id: `section_${sectionData.sectionNumber}_${Math.random().toString(36).substr(2, 9)}`
        });
        console.log('✅ Found section:', sectionData.displayName);
      }
    } catch (error) {
      continue;
    }
  }

  // ── Parse <full_report> tags ──
  const fullReportRegex = /<full_report>\s*(\{[\s\S]*?\})\s*<\/full_report>/gi;
  let fullReportMatch;
  while ((fullReportMatch = fullReportRegex.exec(messageText)) !== null) {
    try {
      let reportData = null;
      try {
        reportData = JSON.parse(fullReportMatch[1].replace(/\n/g, ' ').replace(/\s+/g, ' ').trim());
      } catch (parseError) {
        reportData = attemptJsonFix(fullReportMatch[1]);
      }

      if (reportData) {
        const alreadyFound = fullReports.some(r => r.s3Key === reportData.s3Key);
        if (!alreadyFound) {
          fullReports.push({
            ...reportData,
            type: 'full_report',
            needsPresignedUrl: !reportData.url,
            id: `full_report_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
          });
          console.log('✅ Found full report:', reportData.displayName);
        }
      }
    } catch (error) {
      continue;
    }
  }

  // ── Parse <report_metadata> tags ──
  const metadataRegex = /<report_metadata>\s*(\{[\s\S]*?\})\s*<\/report_metadata>/gi;
  let metadataMatch;
  while ((metadataMatch = metadataRegex.exec(messageText)) !== null) {
    try {
      let metadataData = null;
      try {
        metadataData = JSON.parse(metadataMatch[1].replace(/\n/g, ' ').replace(/\s+/g, ' ').trim());
      } catch (parseError) {
        metadataData = attemptJsonFix(metadataMatch[1]);
      }
      if (metadataData) {
        metadata.push({
          ...metadataData,
          type: 'metadata',
          id: `metadata_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        });
      }
    } catch (error) {
      continue;
    }
  }

  if (sections.length > 0 || fullReports.length > 0 || metadata.length > 0) {
    console.log(`📊 Parsing complete: ${sections.length} sections, ${fullReports.length} reports, ${metadata.length} metadata`);
  }

  return { sections, fullReports, metadata };
};

/**
 * Clean message text by removing all report/prompt tags for display
 */
export const cleanMessageFromReportTags = (messageText) => {
  if (!messageText || typeof messageText !== 'string') {
    return messageText;
  }

  return messageText
    .replace(/<report_section>\s*\{[\s\S]*?\}\s*<\/report_section>/gi, '')
    .replace(/<full_report>\s*\{[\s\S]*?\}\s*<\/full_report>/gi, '')
    .replace(/<report_metadata>\s*\{[\s\S]*?\}\s*<\/report_metadata>/gi, '')
    .replace(/<report_save>\s*\{[\s\S]*?\}\s*<\/report_save>/gi, '')
    .replace(/REPORT_SAVED_TAG::\{[^}]+\}/g, '')
    .replace(/<user_prompt>[\s\S]*?<\/user_prompt>/gi, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

/**
 * Request a presigned URL via WebSocket for reports that need one
 */
export const requestPresignedUrl = (websocket, reportData, userName, sessionId) => {
  if (!websocket || websocket.readyState !== WebSocket.OPEN) {
    console.error('❌ WebSocket not connected, cannot request presigned URL');
    return false;
  }

  const request = {
    action: 'getPresignedUrl',
    s3Key: reportData.s3Key,
    userName: userName,
    sessionId: sessionId || reportData.sessionId,
  };

  console.log('🔗 Requesting presigned URL for:', reportData.s3Key);
  websocket.send(JSON.stringify(request));
  return true;
};

/**
 * Fetch report content from a presigned URL
 */
export const fetchReportContent = async (reportData) => {
  try {
    if (!reportData.url) {
      throw new Error('NO_URL');
    }

    if (reportData.expiresAt && new Date() > new Date(reportData.expiresAt)) {
      throw new Error('URL_EXPIRED');
    }

    const response = await fetch(reportData.url, {
      method: 'GET',
      headers: {
        'Accept': 'text/plain, text/markdown, application/json, */*',
        'Cache-Control': 'no-cache'
      },
      cache: 'no-cache',
      mode: 'cors'
    });

    if (!response.ok) {
      if (response.status === 403) throw new Error('URL_EXPIRED');
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return (await response.text()).trim();
  } catch (error) {
    if (error.message === 'URL_EXPIRED' || error.message === 'NO_URL') {
      return null;
    }
    throw error;
  }
};

/**
 * Check if message contains any report tags
 */
export const hasReportTags = (messageText) => {
  if (!messageText || typeof messageText !== 'string') return false;
  return (
    /<report_section>/i.test(messageText) ||
    /<full_report>/i.test(messageText) ||
    /<report_save>/i.test(messageText) ||
    /REPORT_SAVED_TAG::/i.test(messageText)
  );
};

/**
 * Generate report download filename
 */
export const generateReportFilename = (reportData) => {
  const safeName = (name) => (name || 'report').replace(/[^a-zA-Z0-9]/g, '_');
  
  if (reportData.type === 'section') {
    return `Section_${reportData.sectionNumber}_${safeName(reportData.sectionTitle)}.md`;
  }
  return `${safeName(reportData.displayName || reportData.reportTitle)}_Report.md`;
};

// ── Helper ──
const attemptJsonFix = (jsonString) => {
  try {
    let fixed = jsonString.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim();
    
    const htmlUrlMatch = fixed.match(/<a[^>]+href="([^"]+)"/);
    if (htmlUrlMatch) {
      fixed = fixed.replace(/"url":\s*"<a[^>]*>.*?<\/a>"/g, `"url": "${htmlUrlMatch[1]}"`);
    }

    return JSON.parse(fixed);
  } catch {
    return null;
  }
};